import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from 'firebase/auth';
import { disableNetwork, enableNetwork } from 'firebase/firestore';
import { auth, db, googleProvider } from '../../lib/firebase.js';
import { api, ApiError } from '../../lib/api.js';
import { primeMe, type MeResponse } from '../../lib/mePrefetch.js';
import { isStandalone } from '../../lib/pwa.js';

type AuthStatus = 'loading' | 'signed-out' | 'domain-rejected' | 'signed-in';

interface SessionResponse extends MeResponse {
  /** False when the ID token already carried a current `allowed` claim, so no refresh is needed. */
  claimsUpdated: boolean;
}

interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  rejectedDomainMessage: string | null;
  signInWithGoogle: () => Promise<void>;
  signOutUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Implements the sign-in sequence from docs/PLAN.md §2.2:
 *   1. Google sign-in via Firebase Auth.
 *   2. POST /api/auth/session with the ID token — the API checks the allowlist and, if allowed,
 *      sets the `allowed` custom claim.
 *   3. Not allowed -> DOMAIN_NOT_ALLOWED: sign out immediately, show the rejection screen.
 *   4. Allowed and the claim was just (re)written -> force-refresh the ID token so the new claim
 *      is present before any Firestore read is attempted (Firestore rules require
 *      request.auth.token.allowed == true on every read — see docs/PLAN.md §2.3). On a plain
 *      page reload the token already carries a current claim, and that step is skipped.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [rejectedDomainMessage, setRejectedDomainMessage] = useState<string | null>(null);

  useEffect(() => {
    // Completes a redirect sign-in started from the installed app (see signInWithGoogle). The
    // signed-in user still arrives through onAuthStateChanged below; this only surfaces a failed
    // redirect, which would otherwise leave the sign-in screen silently doing nothing.
    getRedirectResult(auth).catch(() => setStatus('signed-out'));
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setStatus('signed-out');
        return;
      }
      try {
        await establishSession(firebaseUser);
        setUser(firebaseUser);
        setStatus('signed-in');
      } catch (err) {
        if (err instanceof ApiError && err.code === 'DOMAIN_NOT_ALLOWED') {
          setRejectedDomainMessage(err.message);
          await signOut(auth);
          setUser(null);
          setStatus('domain-rejected');
        } else {
          // Unexpected error talking to the API — surface as signed-out rather than a silent
          // half-authenticated state.
          setUser(null);
          setStatus('signed-out');
        }
      }
    });
  }, []);

  async function establishSession(firebaseUser: User): Promise<void> {
    const session = await api.post<SessionResponse>('/auth/session', {});
    // Saves OrgProvider a GET /me round-trip the moment it mounts.
    primeMe(session);
    if (!session.claimsUpdated) return;

    await firebaseUser.getIdToken(true); // force-refresh so the new claim is present

    // Firestore's own credential listener can pick up that refreshed token a beat later than
    // this promise resolves, so a listener started immediately after sign-in (e.g. the
    // dashboard's activity feed) can attach to a still-live stream that's using the pre-claim
    // token, get permission-denied, and never retry — stuck "loading" until a full page reload
    // re-reads the by-then-fresh token from scratch. Forcing the network off/on makes Firestore
    // tear down and reopen its stream right here, so every listener mounted after this resolves
    // is guaranteed to use the fresh, claim-bearing token.
    await disableNetwork(db);
    await enableNetwork(db);
  }

  async function signInWithGoogle(): Promise<void> {
    setStatus('loading');
    // The installed app (iOS especially) can't complete a popup sign-in: the popup opens in a
    // separate browser context that never hands the result back. A full-page redirect stays in
    // the app. It needs VITE_FIREBASE_AUTH_DOMAIN to be this app's own Hosting domain, which
    // serves /__/auth itself — otherwise Safari's storage partitioning loses the result.
    if (isStandalone()) {
      await signInWithRedirect(auth, googleProvider);
      return;
    }
    await signInWithPopup(auth, googleProvider);
    // onAuthStateChanged above handles the rest.
  }

  async function signOutUser(): Promise<void> {
    // Signing out means you're done for now, so don't leave an "online" behind. Best effort: a
    // failure here mustn't stop the sign-out, and the expiry job catches it later anyway.
    await api.put('/me/status', { status: 'offline' }).catch(() => {});
    await signOut(auth);
  }

  return (
    <AuthContext.Provider
      value={{ status, user, rejectedDomainMessage, signInWithGoogle, signOutUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
